import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import equal from 'fast-deep-equal';
import debounce from 'lodash/debounce';
import { useHistory, useLocation } from 'react-router-dom';

import {
  readSavedFilters, removeEmptyFilters, saveFiltersToStorage, syncFiltersToUrl,
} from './query-filter-params-helpers';

type TFiltersType = object;

/*
Корни PII-ключей фильтров (Personally Identifiable Information, т.е. персональные данные): из адресной строки исключаются точные совпадения и семейства
«<корень>.<поле>__<оператор>» (напр. user_data.fio__eq) / «<корень>__<оператор>»
Если в константе пустой массив, то его не удалять - это означает, что сейчас осознанно не исключаем PII-ключи фильтров из url
*/
const DEFAULT_URL_EXCLUDED_KEYS: string[] = [];

export interface FiltersStateContext<T extends TFiltersType> {
  filters: T;
  setFilters: (values: T) => void;
  resetFilters: (preserveKeys?: (keyof T)[]) => void;
  updateFilters: (values: Partial<T>) => void;
  updateFiltersDebounced: (values: Partial<T>) => void;
}

type GenericProviderProps<T extends TFiltersType> = PropsWithChildren<{
  initialData?: T;
  storageKey?: string;
  // Имя своей области URL-параметров «filters.<urlKey>.*», необходим, когда две и более сущности делят одну адресную строку с фильтрами
  urlKey?: string;
  // Корни ключей фильтров с персональными данными, которые не должны попадать в адресную строку
  urlExcludedKeys?: string[];
}>;

interface CreateGenericContextProps {
  displayName: string;
}

type CreateGenericContextReturn<T extends TFiltersType> = [
  () => FiltersStateContext<T>,
  React.FC<GenericProviderProps<T>>,
];

function createGenericFiltersContext<T extends TFiltersType>({
  displayName,
}: CreateGenericContextProps): CreateGenericContextReturn<T> {
  // Create the context instance. The default value should match the type definition.
  // We use undefined here and rely on the provider to always supply a value.
  const FiltersContext = createContext<FiltersStateContext<T> | undefined>(undefined);

  // Custom hook to consume the context
  const useGenericFiltersContext = () => {
    const context = useContext(FiltersContext);
    if (context === undefined) {
      throw new Error(`${displayName} context must be used within a ${displayName}Provider`);
    }
    return context;
  };

  // Provider component
  function FiltersProvider({
    children = undefined,
    initialData = undefined,
    storageKey = undefined,
    urlKey = undefined,
    urlExcludedKeys = DEFAULT_URL_EXCLUDED_KEYS,
  }: GenericProviderProps<T>): React.JSX.Element {
    const location = useLocation();
    const history = useHistory();
    const initialDataRef = useRef<T | undefined>(initialData);

    const [filters, setFilters] = useState<T>(() => {
      const defaultFilters = (initialData ?? {}) as Record<string, unknown>;
      const saved = readSavedFilters(storageKey, urlKey, location.search, defaultFilters);
      if (saved) return { ...initialData, ...saved } as T;
      return initialData ?? {} as T;
    });

    const updateFilters = useMemo(
      () => (values: Partial<T>): void => {
        setFilters((prevValues) => {
          const nextValues = { ...prevValues, ...values };
          return equal(prevValues, nextValues) ? prevValues : (nextValues);
        });
      },
      [],
    );

    const updateFiltersDebounced = useMemo(() => debounce((updateFilters), 200), [updateFilters]);

    const resetFilters = useMemo(
      () => (preserveKeys?: (keyof T)[]): void => {
        setFilters((prevValues) => {
          const preserved = {} as Record<string, unknown>;
          const prevRecord = prevValues as Record<string, unknown>;
          preserveKeys?.forEach((key) => {
            preserved[key as string] = prevRecord[key as string];
          });
          const nextValues = { ...(initialDataRef.current ?? {}), ...preserved } as T;
          return equal(prevValues, nextValues) ? prevValues : nextValues;
        });
      },
      [],
    );

    useEffect(() => {
      const defaultFilters = (initialDataRef.current ?? {}) as Record<string, unknown>;
      const appliedFilters = removeEmptyFilters(filters as Record<string, unknown>);

      if (storageKey) {
        saveFiltersToStorage(storageKey, appliedFilters, defaultFilters);
      }
      syncFiltersToUrl(location, history, urlKey, appliedFilters, defaultFilters, urlExcludedKeys);
    }, [filters, storageKey, urlKey, urlExcludedKeys, location, history]);

    const contextValue = useMemo<FiltersStateContext<T>>(() => {
      const $setFilters = (values: T): void => {
        setFilters((prevValues) => (equal(prevValues, values) ? prevValues : values));
      };

      return {
        filters,
        setFilters: $setFilters,
        resetFilters,
        updateFilters,
        updateFiltersDebounced,
      };
    }, [filters, resetFilters, updateFilters, updateFiltersDebounced]);

    return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
  }

  FiltersProvider.displayName = `${displayName}Provider`;

  return [useGenericFiltersContext, FiltersProvider] as const;
}

export { createGenericFiltersContext };
